import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasPlanAccess } from '../utils/planAccess';

// Navbar Component
const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isInvestmentDropdownOpen, setIsInvestmentDropdownOpen] = React.useState(false);
  const [isUpdatesDropdownOpen, setIsUpdatesDropdownOpen] = React.useState(false);
  const [investmentDropdownTimeout, setInvestmentDropdownTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const [updatesDropdownTimeout, setUpdatesDropdownTimeout] = React.useState<NodeJS.Timeout | null>(null);

  // Cleanup timeouts on component unmount
  React.useEffect(() => {
    return () => {
      if (investmentDropdownTimeout) {
        clearTimeout(investmentDropdownTimeout);
      }
      if (updatesDropdownTimeout) {
        clearTimeout(updatesDropdownTimeout);
      }
    };
  }, [investmentDropdownTimeout, updatesDropdownTimeout]);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  // Check plan access for navigation buttons
  const canAccessShortTerm = hasPlanAccess(user?.subscription_plan, "VECTOR PRO");
  const canAccessSwing = hasPlanAccess(user?.subscription_plan, "EXPERT");
  const canAccessAIAnalyst = hasPlanAccess(user?.subscription_plan, "EXPERT");
  
  // Check if user is a first-time user (hasn't used trial yet)
  const isFirstTimeUser = !user?.has_used_trial;
  
  // Debug logging (remove in production)
  // console.log("Navbar Debug:", {
  //   userPlan: user?.subscription_plan,
  //   canAccessShortTerm,
  //   canAccessSwing,
  //   canAccessAIAnalyst,
  //   isFirstTimeUser
  // });

  return (
    <nav className="bg-white shadow-sm px-4 py-4 lg:px-8 relative z-40">
      <div className="flex justify-between items-center max-w-7xl mx-auto">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/" className="cursor-pointer">
            <img 
              src="/assets/landingpage/logo.png" 
              alt="TrendVector Logo" 
              className="w-auto hover:opacity-80 transition-opacity"
              style={{ height: '85px' }}
            />
          </Link>
        </div>

        {/* Desktop Navigation and CTA Button Grouped Together */}
        <div className="hidden md:flex items-center space-x-6 relative">
          <Link to="/" className="text-blue-700 hover:text-blue-800 font-medium text-lg">Home</Link>
          <Link to="/about" className="text-blue-700 hover:text-blue-800 font-medium text-lg">About Us</Link>
          
          {/* Investment Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => {
              // Open Investment and ensure Updates is closed
              setIsInvestmentDropdownOpen(true);
              setIsUpdatesDropdownOpen(false);
              // Clear any existing timeout
              if (investmentDropdownTimeout) {
                clearTimeout(investmentDropdownTimeout);
                setInvestmentDropdownTimeout(null);
              }
              if (updatesDropdownTimeout) {
                clearTimeout(updatesDropdownTimeout);
                setUpdatesDropdownTimeout(null);
              }
            }}
            onMouseLeave={() => {
              // Set a 1-second timeout before closing the dropdown
              const timeout = setTimeout(() => {
                setIsInvestmentDropdownOpen(false);
              }, 1000);
              setInvestmentDropdownTimeout(timeout);
            }}
          >
            <div className="flex items-center space-x-1 text-blue-700 hover:text-blue-800 font-medium text-lg cursor-pointer">
              <span>Investment</span>
              <span className="text-sm">▼</span>
            </div>
            
            {/* Dropdown Menu */}
            {isInvestmentDropdownOpen && (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-56 bg-slate-700 rounded-lg shadow-lg z-50">
                {/* Pointer Triangle */}
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-slate-700"></div>
                
                {/* Dropdown Items */}
                <Link 
                  to="/long-term-model"
                  onClick={() => setIsInvestmentDropdownOpen(false)}
                  className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer"
                >
                  Long-term model
                </Link>
                
                {/* Short-term model - conditionally disabled */}
                {canAccessShortTerm ? (
                  <Link 
                    to="/short-term-model"
                    onClick={() => setIsInvestmentDropdownOpen(false)}
                    className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer"
                  >
                    Short-term model
                  </Link>
                ) : (
                  <div className="block w-full text-center px-4 py-2 text-gray-400 cursor-not-allowed border-b border-green-500">
                    Short-term model
                    <span className="block text-xs text-gray-500 mt-1">
                      {isFirstTimeUser ? "7-Day Free Trial Available" : "VECTOR PRO+ required"}
                    </span>
                  </div>
                )}
                
                {/* Swing model - conditionally disabled */}
                {canAccessSwing ? (
                  <Link 
                    to="/swing-model"
                    onClick={() => setIsInvestmentDropdownOpen(false)}
                    className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500"
                  >
                    Swing model
                  </Link>
                ) : (
                  <div className="block w-full text-center px-4 py-2 text-gray-400 cursor-not-allowed border-b border-green-500">
                    Swing model
                    <span className="block text-xs text-gray-500 mt-1">
                      {isFirstTimeUser ? "7-Day Free Trial Available" : "EXPERT plan required"}
                    </span>
                  </div>
                )}
                
                {/* NIFTY AI Signal */}
                {isAuthenticated ? (
                  <Link 
                    to="/nifty-signal"
                    onClick={() => setIsInvestmentDropdownOpen(false)}
                    className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500"
                  >
                    NIFTY AI Signal
                  </Link>
                ) : (
                  <div className="block w-full text-center px-4 py-2 text-gray-400 cursor-not-allowed border-b border-green-500">
                    NIFTY AI Signal
                    <span className="block text-xs text-gray-500 mt-1">
                      Login required
                    </span>
                  </div>
                )}
                
                {/* NIFTY Signal Dashboard */}
                {isAuthenticated ? (
                  <Link 
                    to="/tools/nifty-signal-dashboard"
                    onClick={() => setIsInvestmentDropdownOpen(false)}
                    className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500"
                  >
                    NIFTY Signal Dashboard
                  </Link>
                ) : (
                  <div className="block w-full text-center px-4 py-2 text-gray-400 cursor-not-allowed border-b border-green-500">
                    NIFTY Signal Dashboard
                    <span className="block text-xs text-gray-500 mt-1">
                      Login required
                    </span>
                  </div>
                )}

                {/* F&O Trader Tool */}
                {isAuthenticated ? (
                  <Link 
                    to="/fno-trader-tool"
                    onClick={() => setIsInvestmentDropdownOpen(false)}
                    className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500"
                  >
                    F&O Trader Tool
                  </Link>
                ) : (
                  <div className="block w-full text-center px-4 py-2 text-gray-400 cursor-not-allowed border-b border-green-500">
                    F&O Trader Tool
                    <span className="block text-xs text-gray-500 mt-1">
                      Login required
                    </span>
                  </div>
                )}
                
                {/* AI Analyst - conditionally disabled */}
                {canAccessAIAnalyst ? (
                  <Link 
                    to="/ai-analyst"
                    onClick={() => setIsInvestmentDropdownOpen(false)}
                    className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors"
                  >
                    AI Stock Analyst
                  </Link>
                ) : (
                  <div className="block w-full text-center px-4 py-2 text-gray-400 cursor-not-allowed">
                    AI Stock Analyst
                    <span className="block text-xs text-gray-500 mt-1">
                      {isFirstTimeUser ? "7-Day Free Trial Available" : "EXPERT plan required"}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <Link to="/pricing" className="text-blue-700 hover:text-blue-800 font-medium text-lg">Pricing</Link>
          
          {/* Updates Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => {
              // Open Updates and ensure Investment is closed
              setIsUpdatesDropdownOpen(true);
              setIsInvestmentDropdownOpen(false);
              // Clear any existing timeout
              if (updatesDropdownTimeout) {
                clearTimeout(updatesDropdownTimeout);
                setUpdatesDropdownTimeout(null);
              }
              if (investmentDropdownTimeout) {
                clearTimeout(investmentDropdownTimeout);
                setInvestmentDropdownTimeout(null);
              }
            }}
            onMouseLeave={() => {
              // Set a 1-second timeout before closing the dropdown
              const timeout = setTimeout(() => {
                setIsUpdatesDropdownOpen(false);
              }, 1000);
              setUpdatesDropdownTimeout(timeout);
            }}
          >
            <div className="flex items-center space-x-1 text-blue-700 hover:text-blue-800 font-medium text-lg cursor-pointer">
              <span>Updates</span>
              <span className="text-sm">▼</span>
            </div>
            
            {/* Updates Dropdown Menu */}
            {isUpdatesDropdownOpen && (
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-56 bg-slate-700 rounded-lg shadow-lg z-50">
                {/* Pointer Triangle */}
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-slate-700"></div>
                
                {/* Dropdown Items */}
                <Link 
                  to="/news"
                  onClick={() => setIsUpdatesDropdownOpen(false)}
                  className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer"
                >
                  News
                </Link>
                <Link 
                  to="/blogs"
                  onClick={() => setIsUpdatesDropdownOpen(false)}
                  className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer"
                >
                  Blogs
                </Link>
                <Link 
                  to="/tutorials"
                  onClick={() => setIsUpdatesDropdownOpen(false)}
                  className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors cursor-pointer"
                >
                  Tutorials
                </Link>
              </div>
            )}
          </div>
          
          <Link to="/contact" className="text-blue-700 hover:text-blue-800 font-medium text-lg">Contact Us</Link>
          
          {/* Authentication Buttons */}
          {isAuthenticated ? (
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="text-blue-700 hover:text-blue-800 font-medium text-lg">
                Dashboard
              </Link>
              <div className="relative group">
                <button className="flex items-center space-x-1 text-blue-700 hover:text-blue-800 font-medium text-lg">
                  {/* User Profile Icon */}
                  <div className="w-8 h-8 flex items-center justify-center">
                    <img 
                      src="/assets/login/user_profile_icon.svg" 
                      alt="User Profile" 
                      className="w-[1.53rem] h-[1.53rem] object-contain"
                      onError={(e) => {
                        // Fallback to a default user icon if image fails to load
                        const target = e.currentTarget as HTMLImageElement;
                        target.style.display = 'none';
                        
                        const nextSibling = target.nextElementSibling as HTMLElement;
                        if (nextSibling) {
                          nextSibling.style.display = 'block';
                        }
                      }}
                    />
                    {/* Fallback SVG icon */}
                    <svg 
                      className="w-[1.73rem] h-[1.73rem] text-white hidden" 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                  <span className="text-sm">▼</span>
                </button>
                <div className="absolute right-0 mt-2 w-56 bg-slate-700 rounded-lg shadow-lg py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {/* Pointer Triangle */}
                  <div className="absolute -top-2 right-4 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-slate-700"></div>
                  
                  {/* User Information Section */}
                  <div className="px-4 py-3 border-b border-green-500 text-center">
                    <div className="font-semibold text-white">{user?.first_name || 'Username'}</div>
                    <div className="text-sm text-gray-300">{user?.email || 'User Email ID'}</div>
                  </div>
                  
                  {/* Menu Items */}
                  <Link 
                    to="/profile" 
                    className="block px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer text-center"
                  >
                    Profile
                  </Link>
                  <Link 
                    to="/watchlist" 
                    className="block px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer text-center"
                  >
                    Watchlist
                  </Link>
                  <Link 
                    to="/invoice" 
                    className="block px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer text-center"
                  >
                    Invoice
                  </Link>
                  <Link 
                    to="/broker-integrations" 
                    className="block px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer text-center"
                  >
                    Broker Integrations
                  </Link>
                  <Link 
                    to="/portfolio" 
                    className="block px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer text-center"
                  >
                    Portfolio
                  </Link>
                  <Link 
                    to="/orders" 
                    className="block px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer text-center"
                  >
                    Orders
                  </Link>
                  {(user?.role === 'admin' || user?.role === 'super_admin') && (
                    <Link
                      to="/admin/shared-zerodha"
                      className="block px-4 py-2 text-white hover:text-green-400 transition-colors border-b border-green-500 cursor-pointer text-center"
                    >
                      Admin: Shared Zerodha
                    </Link>
                  )}
                  <button 
                    onClick={handleLogout}
                    className="block w-full text-center px-4 py-2 text-white hover:text-green-400 transition-colors cursor-pointer"
                  >
                    Signout
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Link to="/login" className="bg-green-500 hover:bg-green-600 text-white px-7 py-3 rounded-lg font-medium transition-colors ml-4">
              Sign In/Up Now
            </Link>
          )}
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
            <Link 
              to="/" 
              className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/about" 
              className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              About Us
            </Link>
            <Link 
              to="/long-term-model" 
              className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Long-term model
            </Link>
            
            {/* Short-term model - conditionally disabled in mobile */}
            {canAccessShortTerm ? (
              <Link 
                to="/short-term-model" 
                className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Short-term model
              </Link>
            ) : (
              <div className="block px-3 py-2 text-gray-400 cursor-not-allowed">
                Short-term model
                <span className="block text-xs text-gray-500 mt-1">
                  {isFirstTimeUser ? "7-Day Free Trial Available" : "VECTOR PRO+ required"}
                </span>
              </div>
            )}
            
            {/* Swing model - conditionally disabled in mobile */}
            {canAccessSwing ? (
              <Link 
                to="/swing-model" 
                className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Swing model
              </Link>
            ) : (
              <div className="block px-3 py-2 text-gray-400 cursor-not-allowed">
                Swing model
                <span className="block text-xs text-gray-500 mt-1">
                  {isFirstTimeUser ? "7-Day Free Trial Available" : "EXPERT plan required"}
                </span>
              </div>
            )}
            
            {/* NIFTY AI Signal - mobile */}
            {isAuthenticated ? (
              <Link 
                to="/nifty-signal" 
                className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                NIFTY AI Signal
              </Link>
            ) : (
              <div className="block px-3 py-2 text-gray-400 cursor-not-allowed">
                NIFTY AI Signal
                <span className="block text-xs text-gray-500 mt-1">
                  Login required
                </span>
              </div>
            )}
            
            {/* NIFTY Signal Dashboard - mobile */}
            {isAuthenticated ? (
              <Link 
                to="/tools/nifty-signal-dashboard" 
                className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                NIFTY Signal Dashboard
              </Link>
            ) : (
              <div className="block px-3 py-2 text-gray-400 cursor-not-allowed">
                NIFTY Signal Dashboard
                <span className="block text-xs text-gray-500 mt-1">
                  Login required
                </span>
              </div>
            )}

            {/* F&O Trader Tool - mobile */}
            {isAuthenticated ? (
              <Link 
                to="/fno-trader-tool" 
                className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                F&O Trader Tool
              </Link>
            ) : (
              <div className="block px-3 py-2 text-gray-400 cursor-not-allowed">
                F&O Trader Tool
                <span className="block text-xs text-gray-500 mt-1">
                  Login required
                </span>
              </div>
            )}
            
            {/* AI Analyst - conditionally disabled in mobile */}
            {canAccessAIAnalyst ? (
              <Link 
                to="/ai-analyst" 
                className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                AI Stock Analyst
              </Link>
            ) : (
              <div className="block px-3 py-2 text-gray-400 cursor-not-allowed">
                AI Stock Analyst
                <span className="block text-xs text-gray-500 mt-1">
                  {isFirstTimeUser ? "7-Day Free Trial Available" : "EXPERT plan required"}
                </span>
              </div>
            )}
            
            <Link 
              to="/pricing" 
              className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Pricing
            </Link>
            
            {/* Updates Section in Mobile Menu */}
            <div className="px-3 py-2">
              <div className="text-blue-700 font-medium mb-2">Updates</div>
              <div className="ml-4 space-y-1">
                <Link 
                  to="/news" 
                  className="block px-3 py-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  News
                </Link>
                <Link 
                  to="/blogs" 
                  className="block px-3 py-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Blogs
                </Link>
                <Link 
                  to="/tutorials" 
                  className="block px-3 py-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Tutorials
                </Link>
              </div>
            </div>
            
            <Link 
              to="/contact" 
              className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact Us
            </Link>
            
            {/* Mobile Authentication */}
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/profile" 
                  className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Profile
                </Link>
                <Link 
                  to="/watchlist" 
                  className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Watchlist
                </Link>
                <Link 
                  to="/invoice" 
                  className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Invoice
                </Link>
                <Link 
                  to="/broker-integrations" 
                  className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Broker Integrations
                </Link>
                <Link 
                  to="/portfolio" 
                  className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Portfolio
                </Link>
                <Link 
                  to="/orders" 
                  className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Orders
                </Link>
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <Link
                    to="/admin/shared-zerodha"
                    className="block px-3 py-2 text-blue-700 hover:text-blue-800 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Admin: Shared Zerodha
                  </Link>
                )}
                <button 
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 text-red-600 hover:text-red-800 font-medium"
                >
                  Signout
                </button>
              </>
            ) : (
              <Link 
                to="/login" 
                className="block px-3 py-2 bg-green-500 text-white rounded-lg font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign In/Up Now
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
